/**
 * „Podgląd trajektorii" — what the STAGED rule would do over the phase, for a client who delivers
 * and one who does not.
 *
 * ── It is a simulation, and on a template it is a simulation of nobody ──────────────────────
 * There is no client and no logged result behind a plan template, so the „Klient: dowozi / nie
 * dowozi" segment is a what-if on the trainer's own numbers. That is the plan's own decision about
 * the template variant, and it is what keeps the segment meaningful without inventing a verdict the
 * backend does not hold.
 *
 * ── It reads the STAGED rule, unlike the matrix ─────────────────────────────────────────────
 * This is the half of the sheet that answers "what would this do", so it follows every press of the
 * stepper. `trajectory()` projects through the same engine the matrix does, so the two can never
 * disagree about arithmetic — only about which rule they are showing, which is the point.
 */

import { useRef, useState } from 'react';

import {
  bandedGateOf,
  isPercentMode,
  resolveTopIndex,
  type ExerciseDraft,
  type ProgressionRule,
} from '~/entities/plan-draft';
import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';

import { useRovingRadioGroup } from '../lib/useRovingRadioGroup';
import type { LiveMatrixInput } from '../model/executionsToMatrixInputs';
import { formatNumber, type Translate } from '../model/matrixCellState';
import { columnNounKey, formatRuleDelta } from '../model/ruleSentence';
import {
  trajectoryNarration,
  trajectoryRun,
  trajectoryValueAxis,
  type TrajectoryScenario,
} from '../model/trajectory';

import sheetStyles from './ProgressionSheet.module.css';
import { TrajectoryChart, type TrajectoryChartProps } from './TrajectoryChart';
import styles from './TrajectoryPreview.module.css';

const KEY = 'web:planBuilder.progression.sheet';

const SCENARIOS: readonly TrajectoryScenario[] = ['delivers', 'fallsShort'];

/** The locale key naming an axis: its column, or `percent` where the load is prescribed as %1RM. */
function axisKey(axis: { readonly field: MeasurementFieldKey; readonly kind: string }): string {
  return axis.kind === 'percent' ? 'percent' : axis.field;
}

/** How the plot draws the run: a prediction is dashed, an authored projection is not. */
function chartFamilyOf(rule: ProgressionRule | null): TrajectoryChartProps['family'] {
  if (rule === null) return 'none';
  return rule.type === 'Static' ? 'static' : 'fromResult';
}

interface TrajectoryPreviewProps {
  readonly staged: ExerciseDraft;
  readonly field: MeasurementFieldKey;
  readonly cycleLabels: readonly string[];
  /** The absolute cycle under each label, the exercise's lifespan, which the lived record is keyed by. */
  readonly cycles: readonly number[];
  /**
   * What the client has already been given at this slot and what they did with it, or `null` on a
   * template. The plot lays those cycles down as REAL points and simulates only what is ahead.
   */
  readonly live: LiveMatrixInput | null;
  /** The position of the cycle the client is standing in, or `null` on a template. */
  readonly nowIndex: number | null;
  readonly showHints: boolean;
  readonly t: Translate;
  readonly locale: string;
}

export function TrajectoryPreview({
  staged,
  field,
  cycleLabels,
  cycles,
  live,
  nowIndex,
  showHints,
  t,
  locale,
}: TrajectoryPreviewProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [scenario, setScenario] = useState<TrajectoryScenario>('delivers');
  /** „Odtwórz" — bumping it remounts the chart, which is what replays the plot's own entrance. */
  const [replay, setReplay] = useState(0);
  const scenarioRef = useRef<HTMLSpanElement>(null);
  const scenarioGroup = useRovingRadioGroup({
    groupRef: scenarioRef,
    options: SCENARIOS,
    value: scenario,
    onSelect: setScenario,
  });
  /* A CSS media block cannot reach an inline animation delay, so the preference travels as a value.
   * The same reason `shared/design-system/motion` takes it as a parameter everywhere else. */
  const reducedMotion = useScreenMotionPreference();

  /* The VIEWED column's run, lived cycles included, and the axis it is plotted against. The axis is
   * the EXERCISE's (load, else repetitions, else its one progression column), so a node's height
   * does not change meaning when the chips above move. A column with NO rule still has a run, and
   * the mock draws it: every cycle repeats cycle one. What genuinely has nothing to draw is a column
   * no working row prices, which is what a null run means, and `preview.empty` says so. */
  const runInput = { staged, field, live, cycles, scenario };
  const run = trajectoryRun(runInput);
  const valueAxis = run === null ? null : trajectoryValueAxis(runInput, run);
  const rule = run?.rule ?? null;
  const axis = run?.axis ?? null;
  const gate = run?.gate ?? null;
  const plotBand = run?.band ?? null;
  const points = run?.points ?? [];

  /* The unit rides on every label, as the mock's do: a bare "102,5" over a chart with no axis is a
   * number the reader has to guess the denomination of, and the grid two boxes up prints it. The
   * load priced as %1RM is denominated as the grid denominates it, never in the field's kilograms. */
  const unit =
    axis?.kind === 'percent'
      ? t('web:planBuilder.progression.unit.percentOfMax')
      : measurementFieldDefinition(field).unit;
  const formatValue = (raw: number): string => {
    const number = formatNumber(raw * (axis?.displayScale ?? 1), locale);
    return unit === null ? number : `${number} ${unit}`;
  };
  /* Every word over and under the plot, from the one module that also words the sentence at the top
   * of the sheet — see `trajectoryNarration`. The step is the SENTENCE's own text, so the summary
   * cannot state a jump the row above states differently. */
  const gateField = bandedGateOf(staged.progressionRules);
  const narration = trajectoryNarration({
    points,
    field,
    formatValue,
    band: plotBand,
    rule,
    stepText:
      rule === null
        ? ''
        : formatRuleDelta(field, rule, rule.delta ?? 0, isPercentMode(staged.prescriptionMode), locale),
    columnKey: columnNounKey(field, resolveTopIndex(staged.sets) !== -1),
    /* The FIELD, not a noun: the labels print a number in that column's own notation and the
     * summary needs two different words for it, both `ruleSentence`'s to choose. Neither of them is
     * the set table's column header („Powt."), which is an abbreviation with a full stop in it and
     * landed mid-sentence with two of them. */
    gateField: gate?.field ?? gateField,
    cycleLabels,
    t,
    locale,
  });

  return (
    <div className={styles.box}>
      <button
        type="button"
        className={styles.head}
        aria-expanded={isOpen}
        aria-label={t(`${KEY}.preview.${isOpen ? 'collapse' : 'expand'}`)}
        onClick={() => {
          setIsOpen((open) => !open);
        }}
      >
        <span className={sheetStyles.sectionLabel}>{t(`${KEY}.preview.title`)}</span>
        {showHints && (
          <span className={sheetStyles.hint}>
            {t(`${KEY}.preview.hint`, { count: cycleLabels.length })}
          </span>
        )}
        <span className={styles.spring} />
        <span className={styles.chevron} aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className={styles.body}>
          <div className={styles.scenarioRow}>
            <span className={sheetStyles.sectionLabel}>{t(`${KEY}.preview.scenarioLabel`)}</span>
            {/* A single choice, so ONE tab stop and arrows that move it. It claimed `radiogroup`
              * while every option was an ordinary tab stop and no arrow key did anything.
              *
              * It stands over EVERY family, as the mock draws it. It was hidden over a static rule
              * on the reasoning that neither answer moves a number there — true of the numbers, and
              * not of the question: the plot answers „what if they miss" under a static rule with
              * the plan going on regardless, which is exactly the comparison a trainer is making
              * between the two families. Owner's call, 15 September 2026. */}
            <span
              ref={scenarioRef}
              className={styles.segment}
              aria-label={t(`${KEY}.preview.scenarioA11y`)}
              {...scenarioGroup.groupProps}
            >
              {SCENARIOS.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  className={styles.option}
                  {...scenarioGroup.optionProps(candidate)}
                >
                  <span className={styles.optionDot} data-scenario={candidate} aria-hidden="true" />
                  {t(`${KEY}.preview.${candidate}`)}
                </button>
              ))}
            </span>
            <span className={styles.spring} />
            {/* The mock's „↻ Odtwórz". It replays the fold-in of the plot rather than recomputing
              * anything: the run is the same run, and the animation is what a trainer asks to see
              * again after changing a step. The key is what remounts the chart. */}
            <button
              type="button"
              className={styles.replay}
              onClick={() => {
                setReplay((count) => count + 1);
              }}
            >
              <span aria-hidden="true">↻</span> {t(`${KEY}.preview.replay`)}
            </button>
          </div>

          {valueAxis === null ? (
            <span className={sheetStyles.hint}>{t(`${KEY}.preview.empty`)}</span>
          ) : (
            <>
              <div className={styles.plot}>
                <TrajectoryChart
                  key={replay}
                  points={points}
                  family={chartFamilyOf(rule)}
                  labels={narration.labels}
                  cycleLabels={cycleLabels}
                  nowIndex={nowIndex}
                  captionPrescribed={t(`${KEY}.chart.captionPrescribed`)}
                  valueAxis={valueAxis}
                  axisTitle={t(`${KEY}.chart.axis.unit.${axisKey(valueAxis)}`)}
                  formatTick={(tick) => formatNumber(tick, locale)}
                  /* The run in WORDS, built by `trajectoryNarration` from the same labels the plot
                    * draws — see `TrajectoryNarration.runText`. `role="img"` swallows the subtree,
                    * so this name is the whole plot to a screen reader, and the axis rides on it. */
                  ariaLabel={t(`${KEY}.chart.a11y`, {
                    count: cycleLabels.length,
                    run: narration.runText,
                    axis: t(`${KEY}.chart.axis.a11y`, {
                      name: t(`${KEY}.chart.axis.name.${axisKey(valueAxis)}`),
                      unit: t(`${KEY}.chart.axis.unit.${axisKey(valueAxis)}`),
                      min: formatNumber(valueAxis.min, locale),
                      max: formatNumber(valueAxis.max, locale),
                    }),
                  })}
                  reducedMotion={reducedMotion}
                />
              </div>
              {/* The mock's `narrationH`: one sentence under the plot, and it is part of the „?"
                * hints rather than standing on its own — `hideIf` wraps it there exactly as it
                * wraps the head's hint and the matrix's key. */}
              {showHints && <span className={styles.summary}>{narration.summary}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
